import type { DefLookup } from "./catalog";
import type { Cable, SystemNode, Terminal } from "./types";
import { effectiveTerminals } from "./effective";
import { getEffectiveStateOption, getStaticIntraLinkOption } from "./simulate/states";
import type { SimulateContext } from "./cable-current";

/**
 * The engine has no AC model. What it can do honestly with a shore inlet,
 * generator, charger AC-in or inverter AC-out conductor is size it by the
 * ampacity the inlet's breaker demands and refuse to fuse it: ABYC E-11.10.2
 * protects an AC branch with a double-pole breaker, never a fuse in the
 * neutral alone, and a PE conductor carries no load current at all.
 *
 * AC terminals are recognised by the `ac-` id prefix, the same discriminator
 * `detectAcOntoDcRail` uses — `polarity: "neutral"` is not a synonym for AC.
 */
const AC_TERMINAL_ID = /^ac[-_]/i;

export function isAcTerminalId(terminalId: string): boolean {
  return AC_TERMINAL_ID.test(terminalId);
}

/** Canonical AC line and neutral IDs keep their role on edited custom parts. */
export function isAcPowerTerminalId(terminalId: string): boolean {
  return isAcTerminalId(terminalId) && /[-_](?:l[12]?|n)$/i.test(terminalId);
}

/** AC equipment must not inherit the DC battery example for a neutral busbar. */
export function isAcOnlyComponent(definition: { terminals: readonly Pick<Terminal, "id" | "polarity">[] }): boolean {
  return definition.terminals.some((terminal) => isAcTerminalId(terminal.id)) &&
    definition.terminals.every((terminal) =>
      isAcTerminalId(terminal.id) || terminal.polarity === "ground");
}

/** Either end on an AC terminal. Id-based, so it needs no definitions. */
export function isAcCable(cable: Pick<Cable, "fromTerminalId" | "toTerminalId">): boolean {
  return isAcTerminalId(cable.fromTerminalId) || isAcTerminalId(cable.toTerminalId);
}

/** Assumed when no AC part on the run declares a mains voltage. */
export const AC_DEFAULT_NOMINAL_V = 230;

export type AcCableInfo = {
  /** The largest AC terminal rating on the run — the inlet's breaker is what
   *  the conductor has to survive, not the appliance's own draw. 0 when no
   *  AC end carries a finite rating. */
  ratingA: number;
  nominalV: number;
};

/**
 * Null for a DC cable. For an AC cable, the rating its conductor is sized to
 * and the mains voltage to report. A `voltage` under 100 V on an AC part is
 * its DC side (a charger's 12 V) and is not a mains figure.
 */
export function acCableInfo(
  cable: Cable,
  nodesById: Record<string, SystemNode>,
  defLookup: DefLookup,
  cables: readonly Cable[] = [],
  simulate?: SimulateContext,
): AcCableInfo | null {
  if (!isAcCable(cable)) return null;
  let ratingA = 0;
  for (const end of [
    { nodeId: cable.fromNodeId, termId: cable.fromTerminalId },
    { nodeId: cable.toNodeId, termId: cable.toTerminalId },
  ]) {
    if (!isAcTerminalId(end.termId)) continue;
    const node = nodesById[end.nodeId];
    if (node?.kind !== "component") continue;
    const def = defLookup(node.definitionId, node.customDefinition);
    if (!def) continue;
    const term = effectiveTerminals(def, node).find((t) => t.id === end.termId);
    if (term && Number.isFinite(term.maxAmp) && term.maxAmp > ratingA) {
      ratingA = term.maxAmp;
    }
  }
  const nominalV = connectedAcVoltage(cable, nodesById, defLookup, cables, simulate);
  return { ratingA, nominalV: nominalV ?? AC_DEFAULT_NOMINAL_V };
}

/** Resolve metadata along an AC conductor and through passive protection.
 *  Never cross a charger's AC/DC boundary or join distinct AC phases. This is
 *  nominal-voltage propagation, not an AC load-flow/protection calculation. */
function connectedAcVoltage(
  cable: Cable,
  nodesById: Record<string, SystemNode>,
  defLookup: DefLookup,
  cables: readonly Cable[],
  simulate?: SimulateContext,
): number | undefined {
  const queue: Array<[string, string]> = [
    [cable.fromNodeId, cable.fromTerminalId],
    [cable.toNodeId, cable.toTerminalId],
  ];
  const visited = new Set<string>();
  let equipmentVoltage: number | undefined;
  for (let i = 0; i < queue.length; i++) {
    const [nodeId, termId] = queue[i];
    const key = `${nodeId}:${termId}`;
    if (visited.has(key) || !isAcTerminalId(termId)) continue;
    visited.add(key);
    const node = nodesById[nodeId];
    if (node?.kind !== "component") continue;
    const def = defLookup(node.definitionId, node.customDefinition);
    if (!def) continue;
    const terminals = effectiveTerminals(def, node);
    const terminal = terminals.find((t) => t.id === termId);
    if (!terminal) continue;
    const declared = def.acVoltage ?? (def.voltage != null && def.voltage >= 100 ? def.voltage : undefined);
    if (declared != null && Number.isFinite(declared) && declared > 0) {
      // Supply identity wins over a downstream appliance's nominal rating.
      if (def.category === "source" || (def.category !== "distribution" && terminal?.direction === "output")) return declared;
      equipmentVoltage ??= declared;
    }
    for (const run of cables) {
      if (run.fromNodeId === nodeId && run.fromTerminalId === termId) queue.push([run.toNodeId, run.toTerminalId]);
      if (run.toNodeId === nodeId && run.toTerminalId === termId) queue.push([run.fromNodeId, run.fromTerminalId]);
    }
    if (def.category === "distribution") {
      const conductor = termId.toLowerCase().split(/[-_]/).at(-1);
      const option = simulate
        ? getEffectiveStateOption(def, nodeId, simulate)
        : getStaticIntraLinkOption(def);
      const explicit = option?.ampOverride === 0 ? [] : option?.intraLinks;
      for (const t of terminals) {
        // An isolation transformer must not propagate primary voltage into
        // its secondary. Use an explicit pole map when the catalog has one.
        const paired = !explicit || explicit.some(([a, b]) =>
          (a === termId && b === t.id) || (b === termId && a === t.id));
        if (paired && isAcTerminalId(t.id) && t.id.toLowerCase().split(/[-_]/).at(-1) === conductor) queue.push([nodeId, t.id]);
      }
    }
  }
  return equipmentVoltage;
}
